import { cn, NoddiIcon } from 'noddi-ui-common';
import { Accordion as AccordionPrimitive } from 'radix-ui';
import * as React from 'react';

function Accordion(props: Omit<AccordionPrimitive.AccordionMultipleProps, 'type'>) {
  return <AccordionPrimitive.Root data-slot='accordion' type='multiple' {...props} />;
}

function AccordionItem({ className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot='accordion-item'
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  children,
  hideChevron,
  headingLevel = 3,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  hideChevron?: boolean;
  headingLevel?: 2 | 3;
}) {
  const Heading = `h${headingLevel}` as const;
  return (
    <AccordionPrimitive.Header asChild>
      <Heading className='flex'>
        <AccordionPrimitive.Trigger
          data-slot='accordion-trigger'
          className={cn(
            'flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all hover:underline focus-visible:ring-3 focus-visible:ring-ring focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
            className
          )}
          {...props}
        >
          {children}
          {!hideChevron && (
            <NoddiIcon
              name='ChevronDown'
              size='small'
              className='pointer-events-none shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200'
            />
          )}
        </AccordionPrimitive.Trigger>
      </Heading>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({ className, children, ...props }: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot='accordion-content'
      className='overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down'
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
